/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core';
import isEmpty from 'lodash.isempty';
import {translate} from '@ion/core/src/locale/locale';
import AccordionWrapper from '@ion/dls/src/components/accordion/Accordion';
import {IonSpinner} from '@ion/dls/src/components/loading/IonSpinner';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import {EmptyNotifications} from '@ion/dls/src/notifications/EmptyNotifications';
import {UserNotificationDialog} from '@ion/dls/src/notifications/UserNotificationDialog';
import {Notification} from '@ion/dls/src/types/extensions';
import {DLSTheme} from '@ion/dls/src/types/types';
import {getFormattedDate} from '@ion/dls/src/utils/ComponentUtils';
import FilterNotifications from './FilterNotifications';
import {
  getNotificationDetails,
  markAsCleared,
  markAsRead,
  selectNotification,
  setNotificationPanelState
} from './state';

export interface NotificationPanelContentProps {
  checkboxesEnabled?: boolean;
  filterUnread?: boolean;
  isSubAccount?: boolean;
  clearAllVerification?: boolean;
  setClearAllVerification?: (val: boolean) => void;
  clearAllSelectedNotifications?: () => void
  onReviewClick?: (notification: Notification) => void;
  notificationRows?: string;
}

const panelHeight = 'calc(100vh - 192px)';
const panelHeightMobile = 'calc(100vh - 107px)';

const useStyles = makeStyles((theme: DLSTheme) => ({
  loader : {
    height         : 'calc(100vh - 375px)',
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center'
  },
  subHeader : {
    color : '#828282'
  }
}));

/**
 * @function NotificationPanelContent
 * @returns Node
 *
 * Component that houses the contents of the notification
 * drawer that is opened when clicking the icon in the header
 */
const NotificationPanelContent = ({
  checkboxesEnabled = false,
  filterUnread = false,
  isSubAccount = false,
  notificationRows = '100',
  clearAllVerification,
  setClearAllVerification,
  clearAllSelectedNotifications,
  onReviewClick
}: NotificationPanelContentProps) => {
  const classes                   = useStyles();
  const dispatch                  = useDispatch();
  const {isDesktop}               = useMedia();
  const activeTab                 = useSelector(({notificationsPanel}) => notificationsPanel.panel.activePanel);
  const detailsState              = useSelector(({notificationsPanel}) => notificationsPanel.panel.state);
  const selectedNotifications     = useSelector(({notificationsPanel}) => notificationsPanel.panel.selected);
  const {
    all: allNotifications,
    urgent : urgentNotifications,
    loading: isLoading,
    selectedFilter
  }                               = useSelector(({notificationsPanel}) => notificationsPanel);
  const [activeNotifications,
    setActiveNotifications]       = useState([]);
  const [filterNotifications,
    setFilterNotifications]       = useState(null);
  const [notificationIndex,
    setNotificationIndex]         = useState<number>(90);
  const [currentPage,
    setCurrentPage]               = useState<number>(0);
  const [expanded,
    setExpanded]                  = useState<boolean>(true);

  const unreadAllNotifications    = [...allNotifications].filter(notification => !notification.isRead);
  const unreadUrgentNotifications = [...urgentNotifications].filter(notification => !notification.isRead);

  // since the urgent tab's value is 0 we can use a truthy/falsy check
  const emptyTitle = activeTab ? translate('notifications:panel.emptyAll') : translate('notifications:panel.emptyUrgent');

  useEffect(() => {
    setActiveNotifications(activeTab ? allNotifications : urgentNotifications);
  }, []);

  useEffect(() => {
    if (activeTab) {
      filterUnread
        ? setActiveNotifications(unreadAllNotifications)
        : setActiveNotifications(allNotifications);
    } else {
      filterUnread
        ? setActiveNotifications(unreadUrgentNotifications)
        : setActiveNotifications(urgentNotifications);
    }
  }, [filterUnread, activeTab, allNotifications, urgentNotifications]);

  function readMore(notification: Notification) {
    const {uuid} = notification;
    const temp = [...detailsState];
    let updatedTemp;
    const matchingIndex = detailsState.findIndex(item => item.id === uuid);
    if (matchingIndex > -1) {
      temp[matchingIndex] = {id: uuid, open: true, loading: !notification?.details};
      updatedTemp = temp;
      dispatch(setNotificationPanelState(updatedTemp));
    } else {
      dispatch(setNotificationPanelState(
        [...detailsState, {id: uuid, open: true, loading: !notification?.details}]
      ));
    }
    if (!notification?.details) {
      dispatch(getNotificationDetails(notification));
    }
  }

  function readLess(notification: Notification) {
    const {uuid} = notification;
    const temp = [...detailsState];
    let updatedTemp;
    const matchingIndex = detailsState.findIndex(item => item.id === uuid);
    if (matchingIndex > -1) {
      temp[matchingIndex] = {id: uuid, open: false, loading: false};
      updatedTemp = temp;
      dispatch(setNotificationPanelState(updatedTemp));
    } else {
      dispatch(setNotificationPanelState(
        [...detailsState, {id: uuid, open: false, loading: false}]
      ));
    }
  }

  function markNotificationRead(notification: Notification) {
    if (!notification?.isRead) dispatch(markAsRead(notification));
  }

  function onClearClick(notification) {
    dispatch(markAsCleared(notification));
  }

  function handleCheckBoxChange(checked, notification) {
    dispatch(selectNotification(checked, notification));
  }

  useEffect(() => {
    const reponse = destructureNotification(activeNotifications);
    setFilterNotifications(reponse);
  }, [activeNotifications]);

  // function for destructuring the Notification types
  const destructureNotification = (results: Notification[]) => {
    const updates = {};
    results?.forEach((item: Notification) => {
      const context = getFormattedDate(item?.createdOn);
      updates[context] = updates[context] || [];
      updates[context].push(item);
    });
    return updates;
  };

  return (
    <>
      {
        !isLoading ? (
          !isEmpty(filterNotifications) ? Object.keys(filterNotifications).map(key =>
            <AccordionWrapper header={translate('notifications:filterHeader', {key})}
              defaultExpanded={expanded}
              subHeader={<span className={classes.subHeader}>&nbsp;({filterNotifications?.[key].length})</span>}
              details={<FilterNotifications activeNotifications={filterNotifications?.[key]}
                selectedFilter={selectedFilter}
                checkboxesEnabled={checkboxesEnabled}
                selectedNotifications={selectedNotifications}
                detailsState={detailsState}
                emptyTitle={emptyTitle}
                currentPage={currentPage}
                notificationIndex={notificationIndex}
                readMore={readMore}
                readLess={readLess}
                isSubAccount={isSubAccount}
                markNotificationRead={markNotificationRead}
                onClearClick={onClearClick}
                onReviewClick={onReviewClick}
                handleCheckBoxChange={handleCheckBoxChange}
                setNotificationIndex={setNotificationIndex}
                setCurrentPage={setCurrentPage}
                onExpand={setExpanded}
                notificationRows={notificationRows}/>
              }/>
          )
            : <EmptyNotifications title={emptyTitle}
              desktopHeight={panelHeight}
              mobileHeight={panelHeightMobile} />

        ): (
          <div className={classes.loader}>
            <IonSpinner />
          </div>
        )
      }
      {!isLoading && (
        <UserNotificationDialog show={clearAllVerification}
          title={translate('notifications:dialog.clearSelected')}
          message={isDesktop ? translate('notifications:dialog.clearConfirmation')
            : translate('notifications:dialog.confirmation')}
          actionLabel={translate('notifications:dialog.yesClear')}
          onAction={clearAllSelectedNotifications}
          dismissLabel={translate('notifications:dialog.neverMind')}
          onDismiss={function() {
            setClearAllVerification(false);
          }}
          capitalize />
      )}
    </>
  );
};

export default NotificationPanelContent;
